/**
 * EditProfile Page
 *
 * Lets users update profile info and change password, with optimistic UI and
 * clear success/error messaging. Uses Firebase token for authenticated requests.
 */
import React, { useState } from 'react';
import { useAuth } from '../../context/auth';
import ProfileForm from '../../components/account/editProfile/ProfileForm';
import PasswordForm from '../../components/account/editProfile/PasswordForm';
import Footer from '../../components/shared/Footer';
import { SERVER_URL } from '../../api/core';
import axios from 'axios';
import { auth } from '../../firebase/firebase';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import SEOHead from '../../components/shared/SEOHead';

export default function EditProfile() {
  const { user, setUserProfile } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [messages, setMessages] = useState({
    profile: { error: '', success: '' },
    password: { error: '', success: '' },
  });
  const [profileFormKey, setProfileFormKey] = useState(0);

  // Helper function to clear messages after a delay
  const clearMessagesAfterDelay = (section, delay = 5000) => {
    setTimeout(() => {
      setMessages((prev) => ({
        ...prev,
        [section]: { error: '', success: '' },
      }));
    }, delay);
  };

  // Local implementation of updateUserProfile
  const updateUserProfile = async (updates) => {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await axios.put(`${SERVER_URL}/user/update/account`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update the user profile in context
      const updatedUser = { ...user, ...response.data.user };
      setUserProfile(updatedUser);
      
      return updatedUser;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw new Error(error.response?.data?.error || 'Failed to update profile');
    }
  };

  // Local implementation of changePassword
  const changePassword = async (oldPassword, newPassword) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('No user is currently signed in');
      }

      // Re-authenticate the user with their current password
      const credential = EmailAuthProvider.credential(currentUser.email, oldPassword);
      await reauthenticateWithCredential(currentUser, credential);

      // Update the password
      await updatePassword(currentUser, newPassword);
      
      return { success: true };
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        throw new Error('Current password is incorrect');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('New password is too weak. Please choose a stronger password.');
      } else if (error.code === 'auth/requires-recent-login') {
        throw new Error('Please sign out and sign back in before changing your password');
      }
      throw new Error('Failed to change password. Please try again.');
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsUpdating(true);
    setMessages((prev) => ({
      ...prev,
      profile: { error: '', success: '' },
    }));

    try {
      const formData = new FormData(e.target);
      const updates = {
        firstName: formData.get('firstName'),
        lastName: formData.get('lastName'),
      };
      
      const updatedUser = await updateUserProfile(updates);
      
      console.log('Profile updated successfully:', updatedUser);

      setMessages((prev) => ({
        ...prev,
        profile: {
          error: '',
          success:
            'Your profile has been updated successfully! The changes will be reflected immediately.',
        },
      }));

      setProfileFormKey(prev => prev + 1);
      clearMessagesAfterDelay('profile');
    } catch (err) {
      console.error('Update profile error:', err);
      setMessages((prev) => ({
        ...prev,
        profile: {
          error: err.message.includes('Firebase')
            ? 'We encountered an error while updating your profile. Please try again.'
            : err.message,
          success: '',
        },
      }));
      clearMessagesAfterDelay('profile');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setIsChangingPassword(true);
    setMessages((prev) => ({
      ...prev,
      password: { error: '', success: '' },
    }));

    try {
      const formData = new FormData(e.target);
      const oldPassword = formData.get('oldPassword');
      const newPassword = formData.get('newPassword');
      const confirmPassword = formData.get('confirmPassword');

      if (newPassword !== confirmPassword) {
        setIsChangingPassword(false);
        setMessages((prev) => ({
          ...prev,
          password: {
            error: 'New passwords do not match. Please make sure both passwords are identical.',
            success: '',
          },
        }));
        clearMessagesAfterDelay('password');
        return;
      }

      await changePassword(oldPassword, newPassword);

      setMessages((prev) => ({
        ...prev,
        password: {
          error: '',
          success:
            'Your password has been changed successfully! You can now use your new password to sign in.',
        },
      }));
      clearMessagesAfterDelay('password');

    } catch (err) {
      console.error('Change password error:', err);
      setMessages((prev) => ({
        ...prev,
        password: {
          error:
            err.message === 'Current password is incorrect'
              ? 'The current password you entered is incorrect. Please try again.'
              : 'We encountered an error while changing your password. Please try again.',
          success: '',
        },
      }));
      clearMessagesAfterDelay('password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="w-full max-w-[1600px] mx-auto p-6">
      <SEOHead pageType="app-profile" />
      <ProfileForm
        key={profileFormKey}
        user={user}
        isUpdating={isUpdating}
        messages={messages.profile}
        onUpdateProfile={handleUpdateProfile}
      />
      <PasswordForm
        isChangingPassword={isChangingPassword}
        messages={messages.password}
        onChangePassword={handleChangePassword}
      />
      <Footer />
    </div>
  );
}